import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Divider,
  Alert,
  IconButton,
} from '@mui/material';
import {
  Close,
  Google,
  Apple,
  Laptop,
  OpenInNew,
} from '@mui/icons-material';

const platforms = [
  {
    name: 'Google Calendar',
    icon: <Google />,
    color: '#4285F4',
    docUrl: 'https://support.google.com/calendar/answer/37111?hl=hu',
    steps: [
      'Nyissa meg a Google Naptár oldalt.',
      'Kattintson a jobb felső sarokban található Beállítások (fogaskerék) ikonra.',
      'A bal oldali menüben válassza ki az Importálás és exportálás lehetőséget.',
      'Az Exportálás résznél kattintson az Exportálás gombra – letölt egy ZIP fájlt.',
      'Csomagolja ki a ZIP fájlt, majd töltse fel az egyik .ics fájlt itt az oldalon.',
    ],
    note: null,
  },
  {
    name: 'Apple Naptár',
    icon: <Apple />,
    color: '#555555',
    docUrl: 'https://support.apple.com/hu-hu/guide/calendar/icl1023/mac',
    sections: [
      {
        label: 'Mac Naptár alkalmazás (leggyorsabb)',
        steps: [
          'Nyissa meg a Naptár appot a Mac gépén.',
          'A bal oldali sávban kattintson a kívánt naptár nevére.',
          'Válassza a felső menüsorban a Fájl → Exportálás → Exportálás... lehetőséget.',
          'Válassza ki a mentési helyet, és kattintson az Exportálás gombra.',
        ],
      },
      {
        label: 'iCloud.com (számítógépes böngészőből)',
        steps: [
          'Lépjen be az iCloud weboldalra.',
          'Kattintson a bal oldali sávban a megosztani kívánt naptár melletti gombra.',
          'Válassza a Nyilvános naptár opciót.',
          'Másolja ki a megjelenő linket, majd módosítsa a webcal:// előtagot http://-re.',
          'Nyomja meg az Enter billentyűt – a rendszer azonnal letölti a .ics fájlt.',
        ],
      },
    ],
    note: null,
  },
  {
    name: 'Microsoft Outlook',
    icon: <Laptop />,
    color: '#0078D4',
    docUrl: null,
    steps: [
      'Nyissa meg az Outlookot, és kattintson a Naptár ikonra.',
      'Válassza ki a bal oldali listából az exportálni kívánt naptárat.',
      'Kattintson a bal felső sarokban a Fájl menüre.',
      'Válassza a Mentés másként lehetőséget.',
      'A Fájltípus legördülő menüből válassza az iCalendar-formátum (*.ics) lehetőséget.',
      'Kattintson a Mentés gombra. (Időtartam szűréséhez kattintson a További beállítások gombra.)',
    ],
    note: 'Az Outlook exportálási lehetőségei verziónként eltérhetnek.',
  },
];

const ProviderCalendarHelp = ({ open, onClose }) => {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="paper">
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          Hogyan töltse fel naptárát?
        </Box>
        <IconButton
          onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8, color: 'grey.500' }}
        >
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Alert severity="info" sx={{ mb: 3 }}>
          Az ICS (iCalendar) egy szabványos fájlformátum, amelyet minden nagy naptáralkalmazás támogat.
          A feltöltés után az oldalon lévő naptár frissül, és a dátum szűrő figyelembe veszi az importált eseményeket.
        </Alert>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Az ICS fájl csak a dátumokat rögzíti – részletes esemény adatok (ügyfél neve, ár, stb.)
          <strong> nem</strong> kerülnek feltöltésre.
          A duplikálódás elkerülése érdekében ugyanaz a fájl többször feltölthető anélkül, hogy duplumok keletkeznének.
        </Typography>

        {platforms.map((platform, idx) => (
          <Box key={platform.name} sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
              <Box sx={{ color: platform.color }}>{platform.icon}</Box>
              <Typography variant="h6">{platform.name}</Typography>
              {platform.docUrl && (
                <Button
                  size="small"
                  endIcon={<OpenInNew sx={{ fontSize: '0.85rem !important' }} />}
                  href={platform.docUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ ml: 0.5, fontSize: '0.75rem' }}
                >
                  Hivatalos útmutató
                </Button>
              )}
            </Box>

            {platform.sections ? (
              platform.sections.map((section, sIdx) => (
                <Box key={sIdx} sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ mb: 0.75, ml: 1 }}>{section.label}</Typography>
                  <Stepper orientation="vertical" sx={{ ml: 1 }}>
                    {section.steps.map((step, stepIdx) => (
                      <Step key={stepIdx} active completed={false}>
                        <StepLabel>{step}</StepLabel>
                        <StepContent />
                      </Step>
                    ))}
                  </Stepper>
                </Box>
              ))
            ) : (
              <Stepper orientation="vertical" sx={{ ml: 1 }}>
                {platform.steps.map((step, stepIdx) => (
                  <Step key={stepIdx} active completed={false}>
                    <StepLabel>{step}</StepLabel>
                    <StepContent />
                  </Step>
                ))}
              </Stepper>
            )}

            {platform.note && (
              <Alert severity="warning" sx={{ mt: 1, ml: 1 }} icon={false}>
                <Typography variant="caption">{platform.note}</Typography>
              </Alert>
            )}
            {idx < platforms.length - 1 && <Divider sx={{ mt: 2 }} />}
          </Box>
        ))}

        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle2" gutterBottom>Fontos tudnivalók</Typography>
        <Box component="ul" sx={{ pl: 2, m: 0 }}>
          <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
            Csak a <strong>mai naptól</strong> jövőbeli eseményeket importálja a rendszer
          </Typography>
          <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
            Az importált napokat <strong>manuálisan is törölheti</strong> a naptárból, ha mégsem foglalt
          </Typography>
          <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
            Az automatikusan szinkronizált <strong>foglalások</strong> (elfogadott ajánlatok) is megjelennek foglalt napként
          </Typography>
          <Typography component="li" variant="body2">
            Ha a naptára megváltozik, <strong>töltse fel újra</strong> a friss exportot
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="contained">Értettem</Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProviderCalendarHelp;
